import { CSSInterpolation } from '@emotion/serialize'
import { AxiosError } from 'axios'
import { Property } from 'csstype'
import React from 'react'
import { Link, LinkProps } from 'react-router-dom'

import BusyOverlay from 'components/common/BusyOverlay'
import { EmptyState, EmptyStateTitle } from 'components/common/EmptyState'
import Text from 'components/common/Text'
import Pagination from 'components/Pagination'
import { usePaginationWithUrlParam } from 'hooks/use-pagination-with-url-param'
import theme from 'theme'

// Prevent row click if the action comes from the modal or any other element outside of the table
function isClickFromDataTable(event) {
  const table = event.target.closest('[data-table]')
  return Boolean(table)
}

const LinkRow: React.FC<LinkProps> = (props) => <Link {...props} />
const Row: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => (
  <div {...props} />
)

type TableData = any[]
type DataTableError = string | boolean | AxiosError

type DataState = {
  items: any[]
  data: TableData
  loading?: boolean | boolean[]
  error?: DataTableError | DataTableError[]
}

type DataTableProps = {
  className?: string
  displayBorders?: boolean
  data: TableData
  loading?: boolean | boolean[]
  error?: DataTableError | DataTableError[]
  getItemsFromData?: (data: TableData) => any[]
  enablePagination?: boolean
  listStyles?: CSSInterpolation
  renderHeader?: (dataState: DataState) => React.ReactNode
  renderRow: (item: any, index: number, dataState: DataState) => React.ReactNode
  getRowKey?: (item: any, i: number) => React.Key
  renderStaticRow?: () => React.ReactNode
  onRowClick?: (item: any, event: Event) => void
  getRowLinkTo?: (item: any) => LinkProps
  renderEmpty?: (dataState: DataState) => React.ReactNode
  renderError?: (message?: string) => React.ReactNode
  // css grid-template-columns definition
  gridColumns: string
  gridGap?: Property.GridGap | number
  columnTitles?: (string | boolean | React.ReactNode)[]
  wrapRow?: boolean
  itemsPerPage?: number
  updateUrlWithPagination?: boolean
  getSetPage?: (setPage?: React.Dispatch<React.SetStateAction<number>>) => void
  showOverlayOnReload?: boolean
}

export const DataTable: React.FC<DataTableProps> = ({
  className = '',
  data,
  loading,
  displayBorders = false,
  error,
  enablePagination = true,
  gridColumns,
  gridGap = theme.space[3],
  getItemsFromData = (data) => data,
  columnTitles = [],
  listStyles,
  getRowKey = (item, i) =>
    typeof item === 'object' ? item.identifier || item.guid : i,
  renderHeader = () => null,
  getRowLinkTo,
  onRowClick,
  renderRow,
  renderStaticRow,
  renderEmpty = () => null,
  renderError,
  wrapRow = true,
  itemsPerPage = 10,
  updateUrlWithPagination,
  getSetPage = () => null,
  showOverlayOnReload = false,
  ...rest
}) => {
  const items = getItemsFromData(data)
  const isFetching = Array.isArray(loading) ? loading.some(Boolean) : loading
  const { pagination } = usePaginationWithUrlParam(
    items,
    itemsPerPage,
    isFetching,
    updateUrlWithPagination
  )

  // give the parent access to set the page
  if (getSetPage) {
    getSetPage(pagination.changePage)
  }

  const dataState: DataState = React.useMemo(
    () => ({
      items,
      data,
      loading,
      error
    }),
    [data, error, items, loading]
  )

  if (Array.isArray(error) ? error.some((err) => Boolean(err)) : error) {
    return (
      <div css={{ marginTop: theme.space[4] }}>
        {typeof renderError === 'function' ? (
          renderError()
        ) : (
          <EmptyState>
            <EmptyStateTitle>
              {error[0]?.response?.data ||
                'There was an issue loading this page'}
            </EmptyStateTitle>
          </EmptyState>
        )}
      </div>
    )
  }

  const renderDynamicTitle = (title) =>
    typeof title === 'function' ? title(dataState) : title

  const hasItems = Array.isArray(items) && items.length > 0
  const shouldRenderEmpty = !isFetching && !hasItems

  const TableRow = typeof getRowLinkTo === 'function' ? LinkRow : Row
  const itemList = enablePagination ? pagination.subset : pagination.items

  const renderedHeader = renderHeader(dataState)

  const listCss = {
    ...(Boolean(displayBorders && !shouldRenderEmpty) && {
      border: theme.border.base,
      borderTop: 0,
      borderRadius: theme.borderRadius[0],
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0
    }),
    ...(listStyles as any)
  }

  // the reasoning behind not showing the loading spinner when data was loaded successfully once
  // has been lost to file renaming and git history
  // I've added the showOverlayOnReload prop to allow DataTable to always show the loading spinner regardless of prev data
  // so we can also preserve the existing functionality
  const showOverlay = isFetching && (!hasItems || showOverlayOnReload)

  return (
    <div
      data-table
      className={className}
      css={{ position: 'relative' }}
      {...rest}
    >
      {Boolean(renderedHeader) && (
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            paddingTop: theme.space[5],
            paddingBottom: theme.space[5]
          }}
        >
          {renderedHeader}
        </div>
      )}
      <div css={[{ position: 'relative', minHeight: 128 }]}>
        {Boolean(columnTitles?.length && !shouldRenderEmpty) && (
          <div
            css={[
              {
                display: 'grid',
                gridTemplateColumns: gridColumns,
                alignItems: 'center',
                gap: gridGap,
                padding: theme.space[2],
                fontSize: theme.fontSizes[0],
                color: theme.colors.steel[1],
                borderBottom: theme.border.base
              },
              Boolean(displayBorders && !shouldRenderEmpty) && {
                paddingLeft: theme.space[3],
                paddingRight: theme.space[3],
                border: theme.border.base,
                borderRadius: theme.borderRadius[0],
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              }
            ]}
          >
            {columnTitles.map((title, index) => {
              const key = `${title}-${index}`
              if (!title) return <Text key={key} as="div" />
              if (typeof title === 'string') {
                return (
                  <Text
                    key={key}
                    as="h6"
                    css={{ fontSize: theme.fontSizes[1] }}
                  >
                    {renderDynamicTitle(title)}
                  </Text>
                )
              }

              return renderDynamicTitle(title)
            })}
          </div>
        )}
        {shouldRenderEmpty && (
          <div
            css={[
              {
                paddingTop: theme.space[3],
                paddingLeft: theme.space[2],
                paddingBottom: theme.space[3],
                paddingRight: theme.space[2]
              },
              Boolean(displayBorders) && {
                paddingLeft: theme.space[3],
                paddingRight: theme.space[3]
              }
            ]}
          >
            {renderEmpty(dataState)}
          </div>
        )}
        <div css={listCss}>
          {itemList.map((item, i) => {
            const isClickableRow =
              typeof getRowLinkTo === 'function' ||
              typeof onRowClick === 'function'

            return wrapRow ? (
              <TableRow
                key={getRowKey(item, i)}
                className="data-table-row"
                data-table-row={getRowKey(item, i)}
                onClick={
                  typeof onRowClick === 'function'
                    ? (e) => {
                        if (isClickFromDataTable(e)) {
                          return onRowClick(item, e)
                        }
                      }
                    : undefined
                }
                {...(TableRow === LinkRow && {
                  to: getRowLinkTo(item).to
                })}
                {...(TableRow === LinkRow && {
                  state: getRowLinkTo(item).state
                })}
                css={[
                  {
                    display: 'grid',
                    gridTemplateColumns: gridColumns,
                    alignItems: 'center',
                    gap: gridGap,
                    paddingTop: theme.space[4],
                    paddingRight: theme.space[2],
                    paddingBottom: theme.space[4],
                    paddingLeft: theme.space[2],
                    fontSize: theme.fontSizes[0],
                    color: theme.colors.steel[1],
                    borderBottom: theme.border.base,
                    cursor: isClickableRow ? 'pointer' : 'initial',

                    // Note: this really should be using `visibility: hidden` but we can't because
                    // cypress tests won't be able to click it
                    // and cypress can't (yet) trigger hover events
                    '& .visible-on-row-hover': {
                      opacity: 0
                    },

                    '&:hover': {
                      '& .visible-on-row-hover': {
                        opacity: 1 // this should be visibility: visible
                      },
                      ...(isClickableRow && {
                        color: theme.colors.steel[2],
                        background: theme.colors.grayOverlay[0],
                        '& .popover-wrapper': {
                          background: 'white',
                          border: `1px solid ${theme.colors.borderContrast[0]}`,
                          boxShadow: `inset 0 -1px 0 0 ${theme.colors.borderContrast[0]}`
                        },
                        '& button[data-change-state-button]': {
                          color: theme.colors.blue[0],
                          borderColor: 'currentColor',

                          '&:hover': {
                            color: 'white',
                            backgroundColor: theme.colors.blue[0]
                          }
                        }
                      })
                    }
                  },
                  displayBorders && {
                    paddingLeft: theme.space[3],
                    paddingRight: theme.space[3],
                    '&:last-of-type': {
                      borderBottom: 0
                    }
                  }
                ]}
              >
                {renderRow(item, i, dataState)}
              </TableRow>
            ) : (
              renderRow(item, i, dataState)
            )
          })}
          {renderStaticRow &&
            pagination.onLastPage &&
            (wrapRow ? <Row>{renderStaticRow()}</Row> : renderStaticRow())}
        </div>
      </div>
      {enablePagination && pagination.pageCount > 1 && (
        <section className="menu">
          <Pagination {...pagination} />
        </section>
      )}
      {showOverlay && <BusyOverlay shown />}
    </div>
  )
}
